import { useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { HoverPopover } from "@/components/ui";
import { useCloseOnOutsideAndEscape } from "@/components/ui/overlay";
import { IubendaLink } from "@/components/legal/IubendaLink";
import { IubendaCookiePreferencesLink } from "@/components/legal/IubendaCookiePreferencesLink";

/** `!` overrides: the Iubenda embed script restyles `.iubenda-embed` anchors. */
const MENU_ITEM_CLASS =
  "group !m-0 !block w-full !p-0 text-left !text-[13px] !font-normal !leading-5 !text-surface-700 !no-underline";

type SidebarPrivacyMenuProps = {
  onItemClick?: () => void;
  className?: string;
};

export function SidebarPrivacyMenu({
  onItemClick,
  className,
}: SidebarPrivacyMenuProps) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useCloseOnOutsideAndEscape(open, () => setOpen(false), rootRef);
  const label = t("privacyAndCookies");

  const handleItemClick = () => {
    setOpen(false);
    onItemClick?.();
  };

  const trigger = (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      aria-label={label}
      onClick={() => setOpen((isOpen) => !isOpen)}
      className={cn(
        "group flex h-8 w-8 items-center justify-center rounded-md",
        "transition-colors duration-150",
        open
          ? "bg-surface-100 text-surface-900"
          : "text-surface-600 hover:bg-surface-100/70 hover:text-surface-900",
      )}
    >
      <ShieldCheck
        className="h-4 w-4 shrink-0 text-surface-400 group-hover:text-surface-600"
        aria-hidden
      />
    </button>
  );

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      {open ? (
        trigger
      ) : (
        <HoverPopover content={label} placement="above">
          {trigger}
        </HoverPopover>
      )}
      {open ? (
        <div
          role="menu"
          aria-label={label}
          className="absolute bottom-full right-0 z-20 mb-1.5 w-52 animate-fade-in space-y-px overflow-hidden rounded-lg border border-surface-200 bg-surface-0 p-1 shadow-popover"
        >
          <IubendaLink
            policy="privacy"
            role="menuitem"
            className={MENU_ITEM_CLASS}
            title={t("privacyPolicy")}
            onClick={handleItemClick}
          >
            <MenuItemLabel>{t("privacyPolicy")}</MenuItemLabel>
          </IubendaLink>
          <IubendaLink
            policy="cookie"
            role="menuitem"
            className={MENU_ITEM_CLASS}
            title={t("cookiePolicy")}
            onClick={handleItemClick}
          >
            <MenuItemLabel>{t("cookiePolicy")}</MenuItemLabel>
          </IubendaLink>
          <IubendaCookiePreferencesLink
            role="menuitem"
            className={MENU_ITEM_CLASS}
            onClick={handleItemClick}
          >
            <MenuItemLabel>{t("cookiePreferences")}</MenuItemLabel>
          </IubendaCookiePreferencesLink>
        </div>
      ) : null}
    </div>
  );
}

function MenuItemLabel({ children }: { children: ReactNode }) {
  return (
    <span className="flex h-8 w-full min-w-0 items-center truncate rounded-md px-2 transition-colors group-hover:bg-surface-100 group-hover:text-surface-900">
      {children}
    </span>
  );
}
